import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { INTERPRETER_BASE_URL } from "../config/runtime";
import { getSessionId } from "../api/visualizeService";

const SSEContext = createContext();

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error("useSSE must be used within an SSEProvider");
  }
  return context;
};

export const SSEProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const callbacksRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsCountRef = useRef(0);
  const reconnectDelay = 1000; // 1 second
  const attemptReconnectRef = useRef(null);

  const userId = user?.id ?? null;
  const sessionId = userId ? getSessionId(userId) : null;
  const sseUrl = userId
    ? `${INTERPRETER_BASE_URL}/api/sse/${userId}?sessionId=${encodeURIComponent(sessionId)}`
    : null;

  // Register callback for specific topics (called from components to register for specific topics, refer ModelPageUpload.jsx)
  const registerCallback = useCallback((topic, callback) => {
    if (!callbacksRef.current.has(topic)) {
      callbacksRef.current.set(topic, new Set());
    }
    callbacksRef.current.get(topic).add(callback);

    // Return cleanup function
    return () => {
      const callbacks = callbacksRef.current.get(topic);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          callbacksRef.current.delete(topic);
        }
      }
    };
  }, []);

  const handleSSEMessage = useCallback(event => {
    console.log("INTERPRETER_SSE: 📨 Message received:", event.data);
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.log(
        "INTERPRETER_SSE: ⚠️ Failed to parse JSON, using raw data:",
        event.data,
      );
      data = event.data;
    }

    // Handle heartbeat
    if (typeof data === "object" && data.type === "heartbeat") {
      return;
    }

    const isNotification =
      typeof data === "object" &&
      data.type === "notification" &&
      data.topic;

    if (!isNotification) {
      setNotifications(prev => [data, ...prev]);
      return;
    }

    // Store notification
    setNotifications(prev => [data, ...prev]);

    // Drop notifications from a different org than the one currently selected.
    const selectedOrgId = localStorage.getItem("selectedOrganizationId");
    const notifOrgId = data.message?.organization_id;
    if (notifOrgId != null && selectedOrgId != null && String(notifOrgId) !== String(selectedOrgId)) {
      console.log(`INTERPRETER_SSE: ⏭️ Dropping ${data.topic} notification for org ${notifOrgId} (current org: ${selectedOrgId})`);
      return;
    }

    const topic = data.topic;
    const msgSessionId = data.message?.session_id;
    // Visualizer routes by browser session (userId::uuid). TOC/compiler uses anchor ids like model-creator-session.
    if (
      typeof msgSessionId === "string" &&
      msgSessionId.includes("::") &&
      msgSessionId !== getSessionId(userId)
    ) {
      console.log(
        `INTERPRETER_SSE: ⏭️ Dropping ${topic} for session ${msgSessionId}`,
      );
      return;
    }

    const callbacks = callbacksRef.current.get(topic);

    if (callbacks) {
      console.log(
        `INTERPRETER_SSE: ✅ Distributing ${topic} notification to ${callbacks.size} callbacks`,
      );
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(
            `INTERPRETER_SSE: ❌ Error in callback for ${topic}:`,
            error,
          );
        }
      });
    } else {
      console.log(
        `INTERPRETER_SSE: ⚠️ No callbacks registered for topic: ${topic}`,
      );
    }
  }, [userId]);

  const handleSSEOpen = useCallback(() => {
    console.log(
      "INTERPRETER_SSE: ✅ Connection opened for user:",
      userId,
      "URL:",
      sseUrl,
    );
    setIsConnected(true);
    reconnectAttemptsCountRef.current = 0;
  }, [userId, sseUrl]);

  const handleSSEError = useCallback(event => {
    console.error("INTERPRETER_SSE: ❌ Error details:", event);
    setIsConnected(false);
    if (attemptReconnectRef.current) {
      attemptReconnectRef.current();
    }
  }, []);

  const createSSEConnection = useCallback(() => {
    if (!sseUrl) return null;
    try {
      const es = new EventSource(sseUrl, { withCredentials: false });

      es.onopen = handleSSEOpen;
      es.onmessage = handleSSEMessage;
      es.onerror = handleSSEError;

      return es;
    } catch (error) {
      console.error("INTERPRETER_SSE: ❌ Error creating EventSource:", error);
      setIsConnected(false);
      return null;
    }
  }, [sseUrl, userId, handleSSEOpen, handleSSEMessage, handleSSEError]);

  const attemptReconnect = useCallback(() => {
    if (!sseUrl) return;
    reconnectAttemptsCountRef.current += 1;
    console.log(
      `INTERPRETER_SSE: 🔄 Attempting reconnection ${reconnectAttemptsCountRef.current}`,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      // Clean up existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const newConnection = createSSEConnection();
      if (newConnection) {
        eventSourceRef.current = newConnection;
      } else {
        attemptReconnectRef.current?.();
      }
    }, reconnectDelay);
  }, [createSSEConnection, reconnectDelay]);

  // Store the reconnection function in ref to break circular dependency
  attemptReconnectRef.current = attemptReconnect;

  // Called initially on component mount
  useEffect(() => {
    console.log(
      "INTERPRETER_SSE: 🔄 Starting connection setup for user:",
      userId,
      "URL:",
      sseUrl,
    );

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const connection = createSSEConnection();
    if (connection) {
      eventSourceRef.current = connection;
    } else {
      attemptReconnect();
    }

    return () => {
      console.log("INTERPRETER_SSE: 🧹 Cleaning up connection");

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      setIsConnected(false);
    };
  }, [createSSEConnection, attemptReconnect, userId, sseUrl]);

  const value = {
    isConnected,
    notifications,
    registerCallback,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};
